import { AuthProvider, SessionManager } from "@home-health-notify/auth";
import { DomainContext, OrganizationContext } from "hhn-commons";
import { ReactNode, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router";

/**
 * Bridges router + organization context into the auth package's AuthProvider.
 *
 * - `getCurrentLocation` is wired to react-router so the provider re-validates
 *   the session on route changes.
 * - `onLogout` is wired to OrganizationContext.resetData so logging out also
 *   clears the active org / patient state.
 *
 * Also drives the domain state machine: on mount + every route change, calls
 * `domain.checkUserAuthentication()`. This single mechanism handles both
 * saved sessions (Amplify has tokens at boot) and the SSO callback case
 * (Amplify is exchanging `?code=` — `fetchAuthSession` waits for the
 * exchange to complete). Skipped while the domain is already Unauthenticated
 * to avoid that state's `checkUserAuthentication` panic.
 */
type Props = {
  children: ReactNode;
  sessionManager: SessionManager;
};

export function AuthProviderWrapper({ children, sessionManager }: Props) {
  const location = useLocation();
  const { resetData } = useContext(OrganizationContext);
  const { domain } = useContext(DomainContext);
  const lock = useRef(false);

  useEffect(() => {
    // Drive the domain state machine forward on initial mount and route
    // changes. Skipped when the domain is Unauthenticated (its
    // checkUserAuthentication panics if the adapter says authenticated).
    // The lock prevents concurrent checkUserAuthentication calls when
    // React re-fires this effect before the in-flight async chain has
    // finished — without it, two AuthenticatedDomain instances get
    // constructed and both fetch organizations.
    if (domain.domainType === "unauthenticated") return;
    if (lock.current) return;
    lock.current = true;
    domain.checkUserAuthentication().finally(() => {
      lock.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, domain]);

  return (
    <AuthProvider
      sessionManager={sessionManager}
      getCurrentLocation={() => location.pathname}
      onLogin={async () => {
        // External login detected (another *.<env>.homehealthnotify.com
        // app signed in and set the shared cookies). Drive the domain
        // through Unauth → Auth via login() — same path Unauth.login()
        // takes when adapter.isAuthenticated() is already true: it
        // creates AuthenticatedDomain, runs init/fetchOrgs, and
        // transitions. Skipped if domain is anything other than Unauth
        // (initial boot already handles Unknown via the route-change
        // effect above).
        if (domain.domainType === "unauthenticated") {
          await domain.login();
        }
      }}
      onLogout={async () => {
        // Transition the domain to Unauthenticated FIRST (while still
        // authenticated so user.logOut() server call succeeds), THEN
        // clear org-level state. Matches the OLD logout ordering and
        // avoids the two-pass render that produced a logout flicker.
        if (domain.domainType === "authenticated") {
          await domain.logoutUser();
        }
        resetData();
      }}
    >
      {children}
    </AuthProvider>
  );
}
